import { useEffect, useState } from "react";
import { toast } from "sonner";
import { LogOut, Plus, Pencil, Trash2, RefreshCcw, Inbox, CalendarCheck, Caravan } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { formatPrice, statusLabel } from "@/lib/site";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import VehicleForm from "@/components/VehicleForm";

const inputCls = "bg-surface-2/60 border-metallic text-white placeholder:text-slate-500";

export default function Admin() {
  const [user, setUser] = useState(undefined);
  const [tab, setTab] = useState("fordon");
  const [vehicles, setVehicles] = useState([]);
  const [messages, setMessages] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const loadAdminData = () => {
    api.get("/vehicles").then((r) => setVehicles(r.data)).catch(() => {});
    api.get("/admin/messages").then((r) => setMessages(r.data)).catch(() => {});
    api.get("/admin/bookings").then((r) => setBookings(r.data)).catch(() => {});
  };

  useEffect(() => {
    api
      .get("/auth/me")
      .then((r) => {
        setUser(r.data);
        loadAdminData();
      })
      .catch(() => setUser(null));
  }, []);

  const toggleStatus = async (v) => {
    try {
      const { data } = await api.put(`/vehicles/${v.id}`, {
        status: v.status === "salt" ? "tillganglig" : "salt",
      });
      setVehicles((vs) => vs.map((x) => (x.id === v.id ? data : x)));
      toast.success("Status uppdaterad");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const remove = async (v) => {
    if (!window.confirm(`Ta bort "${v.title}"?`)) return;
    try {
      await api.delete(`/vehicles/${v.id}`);
      setVehicles((vs) => vs.filter((x) => x.id !== v.id));
      toast.success("Fordonet har tagits bort");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const saveVehicle = async (values) => {
    setSaving(true);
    try {
      if (editing && editing !== "new") {
        const { data } = await api.put(`/vehicles/${editing.id}`, values);
        setVehicles((vs) => vs.map((x) => (x.id === editing.id ? data : x)));
        toast.success("Fordonet har uppdaterats");
      } else {
        const { data } = await api.post("/vehicles", values);
        setVehicles((vs) => [data, ...vs]);
        toast.success("Fordonet har lagts till");
      }
      setFormOpen(false);
      setEditing(null);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  if (user === undefined) {
    return <main className="pt-40 pb-24 text-center text-slate-400" data-testid="admin-loading">Kollar inloggning...</main>;
  }

  if (user === null) {
    return <LoginScreen onSuccess={(u) => { setUser(u); loadAdminData(); }} />;
  }

  return (
    <main className="pt-28 md:pt-36 pb-24">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div>
            <p className="font-mono-brand text-xs tracking-[0.3em] text-electric mb-3">ADMIN</p>
            <h1 className="font-display font-black tracking-tight text-3xl sm:text-4xl text-white">
              Hantera dina fordon
            </h1>
          </div>
          <Button
            variant="outline"
            data-testid="admin-logout-button"
            onClick={async () => {
              await api.post("/auth/logout").catch(() => {});
              setUser(null);
            }}
            className="rounded-full border-metallic text-slate-300 hover:text-white self-start"
          >
            <LogOut size={15} className="mr-2" />
            Logga ut
          </Button>
        </div>

        <div className="mt-8 flex gap-2 border-b border-metallic/60 pb-px overflow-x-auto" data-testid="admin-tabs">
          {[
            { id: "fordon", label: `Fordon (${vehicles.length})`, icon: Caravan },
            { id: "meddelanden", label: `Meddelanden (${messages.length})`, icon: Inbox },
            { id: "bokningar", label: `Bokningar (${bookings.length})`, icon: CalendarCheck },
          ].map((t) => (
            <button
              key={t.id}
              data-testid={`admin-tab-${t.id}`}
              onClick={() => setTab(t.id)}
              className={`flex items-center gap-2 whitespace-nowrap px-5 py-3 text-sm font-medium border-b-2 -mb-px transition-colors ${
                tab === t.id ? "border-brand text-white" : "border-transparent text-slate-400 hover:text-white"
              }`}
            >
              <t.icon size={15} />
              {t.label}
            </button>
          ))}
        </div>

        {tab === "fordon" && (
          <section className="mt-8" data-testid="admin-vehicles-panel">
            <Button
              data-testid="admin-new-vehicle-button"
              onClick={() => { setEditing("new"); setFormOpen(true); }}
              className="rounded-full bg-brand hover:bg-brand-dark text-white font-semibold"
            >
              <Plus size={16} className="mr-2" />
              Nytt fordon
            </Button>
            <div className="mt-6 space-y-3">
              {vehicles.map((v) => (
                <div
                  key={v.id}
                  data-testid={`admin-vehicle-row-${v.id}`}
                  className="flex flex-col sm:flex-row sm:items-center gap-4 rounded-2xl border border-metallic/70 bg-surface p-4"
                >
                  {v.images?.[0] ? (
                    <img src={v.images[0]} alt="" className="h-16 w-24 rounded-lg object-cover shrink-0" />
                  ) : (
                    <div className="h-16 w-24 rounded-lg bg-surface-2 flex items-center justify-center shrink-0">
                      <Caravan size={20} className="text-slate-600" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="font-display font-bold text-white truncate">{v.title}</p>
                    <p className="text-xs text-slate-400 font-mono-brand mt-0.5">
                      {formatPrice(v.price)} · {v.year}
                    </p>
                  </div>
                  <span
                    data-testid={`admin-vehicle-status-${v.id}`}
                    className={`rounded-full px-3 py-1 text-[11px] font-semibold tracking-widest font-mono-brand border w-fit ${
                      v.status === "salt"
                        ? "bg-red-500/15 text-red-400 border-red-400/30"
                        : "bg-emerald-500/15 text-emerald-400 border-emerald-400/30"
                    }`}
                  >
                    {statusLabel(v.status).toUpperCase()}
                  </span>
                  <div className="flex gap-2 shrink-0">
                    <Button
                      size="sm"
                      variant="outline"
                      data-testid={`admin-toggle-status-${v.id}`}
                      onClick={() => toggleStatus(v)}
                      className="rounded-full border-metallic text-slate-300 hover:text-white"
                    >
                      <RefreshCcw size={14} className="mr-1.5" />
                      {v.status === "salt" ? "Markera tillgänglig" : "Markera såld"}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      data-testid={`admin-edit-${v.id}`}
                      onClick={() => { setEditing(v); setFormOpen(true); }}
                      className="rounded-full border-metallic text-slate-300 hover:text-white"
                    >
                      <Pencil size={14} />
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      data-testid={`admin-delete-${v.id}`}
                      onClick={() => remove(v)}
                      className="rounded-full border-red-400/30 text-red-400 hover:bg-red-500/10"
                    >
                      <Trash2 size={14} />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {tab === "meddelanden" && (
          <section className="mt-8 space-y-3" data-testid="admin-messages-panel">
            {messages.length === 0 && <p className="text-slate-400" data-testid="admin-messages-empty">Inga meddelanden ännu.</p>}
            {messages.map((m) => (
              <div key={m.id} data-testid={`admin-message-${m.id}`} className="rounded-2xl border border-metallic/70 bg-surface p-6">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <p className="font-display font-bold text-white">{m.name}</p>
                  <p className="text-xs text-slate-500 font-mono-brand">{new Date(m.created_at).toLocaleString("sv-SE")}</p>
                </div>
                <p className="mt-1 text-sm text-slate-400">
                  {m.email}
                  {m.phone ? ` · ${m.phone}` : ""}
                </p>
                <p className="mt-3 text-sm text-slate-300 leading-relaxed">{m.message}</p>
              </div>
            ))}
          </section>
        )}

        {tab === "bokningar" && (
          <section className="mt-8 space-y-3" data-testid="admin-bookings-panel">
            {bookings.length === 0 && <p className="text-slate-400" data-testid="admin-bookings-empty">Inga bokningsförfrågningar ännu.</p>}
            {bookings.map((b) => (
              <div key={b.id} data-testid={`admin-booking-${b.id}`} className="rounded-2xl border border-metallic/70 bg-surface p-6">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <p className="font-display font-bold text-white">{b.name}</p>
                  <p className="text-xs text-slate-500 font-mono-brand">
                    {new Date(b.created_at).toLocaleString("sv-SE")}
                  </p>
                </div>
                <p className="mt-1 text-sm text-slate-400">
                  {b.email}
                  {b.phone ? ` · ${b.phone}` : ""}
                </p>
                {b.vehicle && (
                  <p className="mt-2 text-sm text-electric font-medium" data-testid={`admin-booking-vehicle-${b.id}`}>
                    Fordon: {b.vehicle}
                  </p>
                )}
                {b.preferred_date && (
                  <p className="mt-1 text-sm text-slate-300">Önskat datum: {b.preferred_date}</p>
                )}
                {b.message && <p className="mt-3 text-sm text-slate-300 leading-relaxed">{b.message}</p>}
              </div>
            ))}
          </section>
        )}
      </div>

      <Dialog open={formOpen} onOpenChange={(o) => { setFormOpen(o); if (!o) setEditing(null); }}>
        <DialogContent data-testid="vehicle-form-dialog" className="bg-surface border-metallic text-white max-h-[90vh] overflow-y-auto sm:max-w-xl">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl font-bold">
              {editing && editing !== "new" ? "Redigera fordon" : "Nytt fordon"}
            </DialogTitle>
          </DialogHeader>
          <VehicleForm
            initial={editing && editing !== "new" ? editing : null}
            onSubmit={saveVehicle}
            onCancel={() => { setFormOpen(false); setEditing(null); }}
            submitting={saving}
          />
        </DialogContent>
      </Dialog>
    </main>
  );
}

function LoginScreen({ onSuccess }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      onSuccess(data);
      toast.success("Välkommen!");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="pt-32 md:pt-40 pb-24">
      <div className="mx-auto max-w-md px-5">
        <div className="rounded-3xl border border-metallic/70 bg-surface p-8 md:p-10" data-testid="admin-login-card">
          <p className="font-mono-brand text-xs tracking-[0.3em] text-electric mb-3">ADMIN</p>
          <h1 className="font-display font-black tracking-tight text-3xl text-white">Logga in</h1>
          <p className="mt-2 text-sm text-slate-400">Logga in för att hantera dina annonser.</p>
          <form onSubmit={submit} className="mt-8 space-y-4" data-testid="admin-login-form">
            <div className="space-y-1.5">
              <Label htmlFor="admin-email" className="text-slate-300">E-post</Label>
              <Input
                id="admin-email"
                data-testid="admin-email-input"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="din@epost.se"
                className={inputCls}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="admin-password" className="text-slate-300">Lösenord</Label>
              <Input
                id="admin-password"
                data-testid="admin-password-input"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className={inputCls}
              />
            </div>
            {error && (
              <p className="rounded-lg bg-red-500/10 border border-red-400/30 px-4 py-3 text-sm text-red-400" data-testid="admin-login-error">
                {error}
              </p>
            )}
            <Button
              type="submit"
              data-testid="admin-login-button"
              disabled={loading}
              className="w-full rounded-full bg-brand hover:bg-brand-dark text-white font-semibold py-3"
            >
              {loading ? "Loggar in..." : "Logga in"}
            </Button>
          </form>
        </div>
      </div>
    </main>
  );
}
